import { useState, useEffect } from 'react';

// Functie om RGB-waarden om te zetten naar een hex-kleurcode (bv. #ffffff)
function rgbToHex(r, g, b) {
  return '#' + [r, g, b].map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
}

// Cache per URL: elke bag decodeerde dezelfde afbeelding anders opnieuw,
// en bij een remount (accordion, memo-miss) zou het werk opnieuw gebeuren
const colorCache = new Map();

export function useImageBackgroundColor(imageUrl) {
  const [backgroundColor, setBackgroundColor] = useState(() => colorCache.get(imageUrl) ?? null);

  useEffect(() => {
    if (!imageUrl) return;

    if (colorCache.has(imageUrl)) {
      setBackgroundColor(colorCache.get(imageUrl));
      return;
    }

    // Reset de kleur voor een nieuwe afbeelding
    setBackgroundColor(null);
    let cancelled = false;

    const img = new Image();
    // Belangrijk voor afbeeldingen van andere domeinen (zoals bricklink)
    img.crossOrigin = 'Anonymous';

    img.onload = () => {
      if (cancelled) return;
      try {
        // We hebben alleen pixel (0,0) nodig — teken 1x1 i.p.v. de hele afbeelding
        const canvas = document.createElement('canvas');
        canvas.width = 1;
        canvas.height = 1;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, 1, 1, 0, 0, 1, 1);

        const pixelData = ctx.getImageData(0, 0, 1, 1).data;

        if (pixelData[3] === 0) {
          colorCache.set(imageUrl, null);
          return;
        }
        // Converteer de RGBA-waarden naar een hex-kleur
        const hexColor = rgbToHex(pixelData[0], pixelData[1], pixelData[2]);

        colorCache.set(imageUrl, hexColor);
        setBackgroundColor(hexColor);
      } catch (e) {
        // getImageData gooit bij een tainted canvas (ontbrekende CORS headers)
        colorCache.set(imageUrl, null);
      }
    };

    img.onerror = () => {
      if (cancelled) return;
      // Als de afbeelding niet laadt, gebruik een standaardkleur
      colorCache.set(imageUrl, '#f8f9fa'); // Mantine's default gray
      setBackgroundColor('#f8f9fa');
    };

    img.src = imageUrl;

    return () => {
      // Voorkom setState na unmount / bij een gewijzigde URL
      cancelled = true;
      img.onload = null;
      img.onerror = null;
    };
  }, [imageUrl]); // Voer dit effect opnieuw uit als de imageUrl verandert

  return backgroundColor;
}
